import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qsmncnlsngrm4lm5b0';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('mobile_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'mobile_alt')
            .map(([key, value]) => [key, value as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        const msg =
          (parsed as { meta?: { message?: string } }).meta?.message ||
          (parsed as { message?: string }).message ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 黑料在线看',
          description: '了解黑料在线看的品牌故事、核心优势与联系方式。我们致力于为读者提供真实、及时的娱乐资讯。',
          url: 'https://heiliaozx.com/about',
          isPartOf: { '@type': 'WebSite', name: '黑料在线看', url: 'https://heiliaozx.com' },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliaozx.com/' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-10">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            {/* Hero */}
            <section className="py-10 md:py-14 text-center">
              <div className="inline-flex items-center gap-2 bg-primary-500/20 border border-primary-500/30 rounded-full px-4 py-1.5 mb-5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-fire-fill text-primary-400 text-sm"></i>
                </div>
                <span className="text-primary-300 text-sm font-medium whitespace-nowrap">关于我们</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-50 mb-5">
                黑料在线看
              </h1>
              <p className="text-foreground-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                汇聚娱乐圈、网红、明星最新独家内幕与爆料资讯。我们坚信，真相值得被看见。
              </p>
            </section>

            {/* Brand Introduction */}
            <section className="py-8 md:py-12" aria-label="品牌介绍">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">品牌介绍</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-4">
                    黑料在线看成立于2024年，是国内领先的娱乐资讯爆料平台。我们致力于为读者提供娱乐圈、网红圈、明星圈的最新独家内幕与爆料资讯。
                  </p>
                  <p className="text-foreground-300 text-sm md:text-base leading-relaxed mb-4">
                    在信息爆炸的时代，真假信息混杂。黑料在线看坚持<b className="text-foreground-100">"绝不删改，直击真相"</b>的原则，每一条发布的内容都经过专业团队的严格核实与交叉验证，确保读者获得的信息真实可靠。
                  </p>
                  <p className="text-foreground-300 text-sm md:text-base leading-relaxed">
                    我们的内容涵盖明星黑料、网红爆料、娱乐圈内幕、黑料视频、独家爆料等五大核心板块，每天实时更新，为读者提供一站式的娱乐资讯阅读体验。
                  </p>
                </div>
                <div className="relative h-64 md:h-80 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20dark%20office%20space%20with%20dramatic%20red%20accent%20lighting%2C%20journalism%20and%20media%20theme%2C%20professional%20atmosphere%2C%20sleek%20desk%20with%20screens%2C%20urban%20night%20view%20through%20windows%2C%20cinematic%20noir%20style%2C%20no%20people&width=800&height=600&seq=hl-about-brand&orientation=landscape"
                    alt="黑料在线看品牌形象"
                    title="黑料在线看 - 专业娱乐资讯平台"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Core Values */}
            <section className="py-8 md:py-12" aria-label="核心优势">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">为什么选择我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-shield-check-line', title: '真实可靠', desc: '多轮交叉验证，确保每一条信息的真实性和准确性' },
                  { icon: 'ri-flashlight-line', title: '独家首发', desc: '拥有覆盖全行业的爆料网络，第一时间获取独家内幕' },
                  { icon: 'ri-refresh-line', title: '实时更新', desc: '7×24小时全天候监控，重大事件即时跟进报道' },
                  { icon: 'ri-lock-line', title: '严格保密', desc: '对爆料人信息严格保密，安全可靠的匿名投稿渠道' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg border border-background-200/50 p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-4">
                      <i className={`${item.icon} text-primary-400 text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-100 mb-2">{item.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation / Stats */}
            <section className="py-8 md:py-12" aria-label="平台数据">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">平台口碑</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                {[
                  { number: '5000+', label: '独家爆料' },
                  { number: '1200万+', label: '月活跃读者' },
                  { number: '98.7%', label: '信息准确率' },
                  { number: '24/7', label: '全天候更新' },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="bg-background-100 rounded-lg border border-background-200/50 p-6 text-center hover:border-accent-500/30 transition-colors"
                  >
                    <div className="text-2xl md:text-3xl font-heading text-accent-400 mb-2">{stat.number}</div>
                    <div className="text-foreground-500 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="py-8 md:py-12" aria-label="用户评价">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">读者评价</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { name: '资深娱记老王', text: '关注黑料在线看快两年了，信息更新速度确实快，很多独家消息比同行媒体早了好几天。做我们这行的都知道，这种时效性有多难得。' },
                  { name: '娱乐圈观察者', text: '之前对某些爆料半信半疑，后来好几个被实锤验证了，不得不说他们的消息源确实靠谱。现在已经成为我每天必刷的资讯平台。' },
                  { name: '忠实读者小李', text: '从朋友推荐关注到现在，已经成了忠实读者。内容质量高，没有标题党，每篇文章都有干货。最喜欢看独家爆料栏目，次次都有惊喜。' },
                ].map((t, idx) => (
                  <div key={idx} className="bg-background-100 rounded-lg border border-background-200/50 p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-400 text-xs"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-foreground-300 text-sm leading-relaxed mb-4">
                      "{t.text}"
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-500/20">
                        <i className="ri-user-line text-secondary-400 text-xs"></i>
                      </div>
                      <span className="text-foreground-400 text-xs">{t.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="py-8 md:py-12" aria-label="联系我们">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">爆料投稿</h2>
              <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8 max-w-2xl">
                <p className="text-foreground-400 text-sm mb-6">
                  你有独家猛料？欢迎通过以下表单提交爆料。我们承诺对爆料人信息<b className="text-foreground-200">严格保密</b>。
                </p>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="mobile_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-form-extra"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-300 text-xs mb-1.5">
                        称呼 <span className="text-foreground-500">(选填)</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        className="w-full bg-background-200 border border-background-300/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="你的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-300 text-xs mb-1.5">
                        邮箱 <span className="text-foreground-500">(选填)</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="w-full bg-background-200 border border-background-300/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-category" className="block text-foreground-300 text-xs mb-1.5">
                      爆料分类
                    </label>
                    <select
                      id="contact-category"
                      name="category"
                      className="w-full bg-background-200 border border-background-300/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 focus:outline-none focus:border-primary-500/50 transition-colors"
                    >
                      <option value="">请选择分类</option>
                      <option value="明星黑料">明星黑料</option>
                      <option value="网红爆料">网红爆料</option>
                      <option value="娱乐圈内幕">娱乐圈内幕</option>
                      <option value="黑料视频">黑料视频</option>
                      <option value="独家爆料">独家爆料</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-content" className="block text-foreground-300 text-xs mb-1.5">
                      爆料内容 <span className="text-primary-400">*</span>
                    </label>
                    <textarea
                      id="contact-content"
                      name="content"
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full bg-background-200 border border-background-300/50 rounded-lg px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                      placeholder="请详细描述你的爆料内容（限500字）..."
                    ></textarea>
                    <p className="text-foreground-600 text-xs mt-1">限500字以内</p>
                  </div>

                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap"
                  >
                    提交爆料
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-accent-400 text-sm mt-3">爆料已成功提交，感谢你的贡献！我们会尽快核实处理。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-primary-400 text-sm mt-3">{formError || '提交失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </section>

            {/* Contact Info */}
            <section className="py-8 md:py-12" aria-label="联系方式">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-50 mb-6">联系方式</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-mail-line text-primary-400 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-sm text-foreground-100 mb-1">爆料邮箱</h3>
                  <p className="text-foreground-500 text-xs">tips@heiliaozx.com</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-customer-service-line text-primary-400 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-sm text-foreground-100 mb-1">合作洽谈</h3>
                  <p className="text-foreground-500 text-xs">bd@heiliaozx.com</p>
                </div>
                <div className="bg-background-100 rounded-lg border border-background-200/50 p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className="ri-wechat-line text-primary-400 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-sm text-foreground-100 mb-1">微信客服</h3>
                  <p className="text-foreground-500 text-xs">heiliaozx_official</p>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}